import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectionGroupValue } from 'fronton-react/selection-group/types';
import { Adaptive, SelectionGroup, SelectionGroupItem } from 'fronton-react';
import { TFrontendConfigurationItem } from 'entries/common/widgets/context';
import { AdaptiveInput } from 'pages/common/components/AdaptiveInput';
import { getLabelText } from '../../helpers';
import { Hint } from '../Hint';
import { InputContainer, InputContainerColumn, InputContainerRow, HintContainer } from './styles';
import { getValidationRules } from './helpers/get-validation-rules';

type TProps = {
  frontendConfiguration: TFrontendConfigurationItem[] | null;
};

export const FrontendConfigurationFields = ({ frontendConfiguration }: TProps) => {
  const [selectedPresets, setSelectedPresets] = useState<{ [name: string]: SelectionGroupValue }>({});
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const handleChangeFrontendConfigurationField = ({
    onChange,
    paramName,
  }: {
    onChange: (value: string) => void;
    paramName: string;
  }) => (value: string) => {
    setSelectedPresets(prevState => ({
      ...prevState,
      [paramName]: '',
    }));
    onChange(value);
  };

  const handleChangePresetValue = (paramName: string) => (
    _: React.MouseEvent<Element, MouseEvent>,
    value: SelectionGroupValue
  ) => {
    setValue(paramName, value);
    setSelectedPresets(prevState => ({
      ...prevState,
      [paramName]: value,
    }));
  };

  if (!frontendConfiguration) {
    return null;
  }

  return (
    <InputContainer>
      {frontendConfiguration.map(({ name, label, presets, hints, type, validations }) => {
        const hasHints = !!hints && hints.length > 0;
        const hasPresets = !!presets && presets.length > 0;
        const rules = getValidationRules(validations);

        return (
          <InputContainerRow key={name}>
            <InputContainerColumn>
              <Controller
                name={name}
                control={control}
                {...(rules && { rules })}
                render={({ field: { value, onChange } }) => (
                  <>
                    <Adaptive to="tablet">
                      <AdaptiveInput
                        form={{
                          value,
                          onChange: handleChangeFrontendConfigurationField({ onChange, paramName: name }),
                          errors,
                        }}
                        name={name}
                        locale={{
                          labels: {
                            [name]: getLabelText(label),
                          },
                        }}
                        mobile
                        type={type}
                      />
                    </Adaptive>
                    <Adaptive from="tablet">
                      <AdaptiveInput
                        form={{
                          value,
                          onChange: handleChangeFrontendConfigurationField({ onChange, paramName: name }),
                          errors,
                        }}
                        name={name}
                        locale={{
                          labels: {
                            [name]: getLabelText(label),
                          },
                        }}
                        mobile={false}
                        type={type}
                      />
                    </Adaptive>
                    {hasHints ? (
                      <HintContainer data-testid={`${name}-field-hint`}>
                        <Hint content={hints} />
                      </HintContainer>
                    ) : null}
                  </>
                )}
              />
            </InputContainerColumn>
            {hasPresets ? (
              <SelectionGroup
                rounded
                size="s"
                active={selectedPresets[name]}
                onChange={handleChangePresetValue(name)}>
                {presets.map(preset => (
                  <SelectionGroupItem key={String(preset.value)} value={String(preset.value)}>
                    {getLabelText(preset.label)}
                  </SelectionGroupItem>
                ))}
              </SelectionGroup>
            ) : null}
          </InputContainerRow>
        );
      })}
    </InputContainer>
  );
};
